<script setup>
import SelectOptionFactory from '@/modules/select_option/SelectOptionFactory.js'
import { WALKER_COMMANDS } from '@/services/helpers/ElbwalkerHelper'
import { useCartStore } from '@/stores/cart'
import { useGuestCheckoutStore } from '@/stores/guestCheckout'
import { elb } from '@elbwalker/walker.js'
import { getCurrentInstance, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const cartStore = useCartStore()
const guestStore = useGuestCheckoutStore()

const emits = defineEmits(['success', 'showLogin'])

const { appContext } = getCurrentInstance()
const translate = appContext.config.globalProperties.translate

const props = defineProps({
  emailRegex: RegExp
})

const router = useRouter()
const route = useRoute()

const title = ref(null)
const firstName = ref('')
const lastName = ref('')
const gender = ref('')
const email = ref('')
const loading = ref(false)
const form = ref(null)

const titleRef = ref(null)
const firstNameRef = ref(null)
const lastNameRef = ref(null)
const genderRef = ref(null)
const emailRef = ref(null)
const submitButtonRef = ref(null)
const titles = SelectOptionFactory.getTitleOptions()
const rules = {
  required: (value) => !!value || translate('validation_msg_required'),
  email: (value) =>
    props.emailRegex.test(value) || translate('validation_msg_email')
}

const el = ref(null)

onMounted(() => {
  elb(WALKER_COMMANDS.init, el.value)
})

const validateForm = async () => {
  const formInstance = form.value
  if (!formInstance) return false
  const validationResult = await formInstance.validate()
  return validationResult.valid
}

const storeUserDataForCheckout = (redirectUrl) => {
  if (
    !redirectUrl.includes('/checkout/guest') &&
    !redirectUrl.includes('/checkout/v2')
  )
    return

  guestStore.email = email.value

  const partialAddressData = {
    first_name: firstName.value,
    last_name: lastName.value,
    gender: gender.value,
    title: title.value
  }

  guestStore.initialAddressData = partialAddressData
}

const guestAction = async () => {
  const isFormValid = await validateForm()
  if (!isFormValid) return

  loading.value = true

  try {
    await cartStore.refreshCart()

    const redirectUri = '/checkout/guest'
    storeUserDataForCheckout(redirectUri)

    await router.push(redirectUri)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div
    ref="el"
    class="d-flex flex-column justify-center"
    data-elb="account"
    data-elb-account="action:guest"
    data-elbaction="visible"
  >
    <VForm
      ref="form"
      lazy-validation
      class="d-flex flex-column justify-center mt-5"
      @submit.prevent="guestAction"
    >
      <VSelect
        ref="titleRef"
        v-model="title"
        :label="translate('opc_address_title_label')"
        :items="titles"
        item-value="value"
        item-title="title"
        clearable
        outlined
        class="pb-3"
        @keydown.enter.prevent="firstNameRef?.focus()"
      />

      <div class="d-flex flex-column flex-md-row py-3">
        <div class="d-flex flex-column flex-grow-1 pr-md-2 pr-0">
          <VTextField
            ref="firstNameRef"
            v-model="firstName"
            :rules="[rules.required]"
            :label="translate('opc_address_firstname_label')"
            @keydown.enter.prevent="lastNameRef?.focus()"
          />
        </div>

        <div class="d-flex flex-column flex-grow-1 pl-md-2 pl-0">
          <VTextField
            ref="lastNameRef"
            v-model="lastName"
            :rules="[rules.required]"
            :label="translate('opc_address_lastname_label')"
            @keydown.enter.prevent="
              genderRef?.$el?.querySelector('input[value=female]')?.focus() ||
              genderRef?.$el?.querySelector('input[value=male]')?.focus() ||
              emailRef?.focus()
            "
          />
        </div>
      </div>
      <div class="d-flex gap-4 py-3">
        <VRadioGroup
          ref="genderRef"
          :label="`${translate('opc_address_salutation_label')}:`"
          v-model="gender"
          :rules="[rules.required]"
          inline
          @keydown.enter.prevent="emailRef?.focus()"
        >
          <VRadio :label="translate('gender_female_label')" value="female" />
          <VRadio :label="translate('gender_male_label')" value="male" />
        </VRadioGroup>
      </div>

      <VTextField
        ref="emailRef"
        type="email"
        v-model="email"
        :label="translate('fe_app_label_email')"
        :rules="[rules.required, rules.email]"
        class="mt-3"
        @keydown.enter.prevent="guestAction()"
      />

      <VBtn
        ref="submitButtonRef"
        class="mt-5"
        @click="guestAction"
        :loading="loading"
        :disabled="loading"
        block
      >
        {{ translate('order_as_guest') }}
      </VBtn>
      <span class="mt-3 text-center">{{
        translate('auth_you_already_have_an_account')
      }}</span>
      <VBtnSecondary
        @click="emits('showLogin')"
        class="mt-3"
        :disabled="loading"
        block
      >
        {{ translate('login') }}
      </VBtnSecondary>
    </VForm>
  </div>
</template>

<style scoped>
:deep(.v-radio-group > .v-input__control > .v-label) {
  margin-inline-start: 0px;
}
</style>
